import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { TextField, Button, FormControl } from "@mui/material";
import { api } from "../../../utils/api";
import { fetchVehicleDetails } from "../../../utils/fetchVehicleDetails";
import {
  getFieldsFromNevdisDetails,
  VEHICLE_TYPE_OPTIONS
} from "../../../utils/mapNevdisBodyType";
import { debounce } from "lodash";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  getVehicleStateFieldLabel,
  getVehicleStateOptionsForSelect
} from "../../../utils/p1Region";

const validationSchema = Yup.object().shape({
  vehicleDetails: Yup.object().shape({
    vehicleState: Yup.string().required("Vehicle state or region is required"),
    vehicleRegistration: Yup.string().required("Vehicle Registration is required"),
    vin: Yup.string()
      .matches(/^[a-zA-Z0-9]{17}$/, "VIN must be exactly 17 alphanumeric characters")
      .required("VIN is required"),
    make: Yup.string().required("Make is required"),
    model: Yup.string().required("Model is required"),
    color: Yup.string(),
    vehicleType: Yup.string().oneOf(["", "sedan", "wagon", "4wd"])
  })
});

const ActivateCustomerForm = ({ customer, callback, closeModal }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Get existing vehicle data if available
  const existingVehicle = customer.vehicles && customer.vehicles.length > 0 && customer.vehicles[0] 
    ? customer.vehicles[0] 
    : null;

  const initialValues = {
    vehicleDetails: {
      vehicleState: existingVehicle?.state || "",
      vehicleRegistration: existingVehicle?.rego || "",
      vin: existingVehicle?.vin || "",
      make: existingVehicle?.make || "",
      model: existingVehicle?.model || "",
      color: existingVehicle?.color || "",
      vehicleType: existingVehicle?.vehicleType || ""
    }
  };

  const handleVehicleStateChange = debounce(async (setFieldValue, rego, state) => {
    if (rego && state) {
      try {
        const vehicleDetails = await fetchVehicleDetails(rego, state);
        if (vehicleDetails) {
          console.log("Fetched From NDVIS:", vehicleDetails);
          const fields = getFieldsFromNevdisDetails(vehicleDetails);
          setFieldValue("vehicleDetails.vin", fields.vin);
          setFieldValue("vehicleDetails.make", fields.make);
          setFieldValue("vehicleDetails.model", fields.model);
          if (fields.color) setFieldValue("vehicleDetails.color", fields.color);
          if (fields.vehicleType) setFieldValue("vehicleDetails.vehicleType", fields.vehicleType);
        }
      } catch (error) {
        console.error("Error fetching vehicle details:", error);
        // Don't show error, just allow manual entry
      }
    }
  }, 500);

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    setError("");

    try {
      // Check for invite codes with inactive vehicles to find the correct vehicle with products
      let vehicleId = null;
      let inviteCodeVehicle = null;

      try {
        const inviteCodeResponse = await api("v1/inviteCode/search", "POST", {
          customer: customer._id
        });
        
        if (inviteCodeResponse.data.success && inviteCodeResponse.data.data.searchResult.length > 0) {
          // Find the first invite code with an inactive vehicle
          for (const inviteCode of inviteCodeResponse.data.data.searchResult) {
            if (inviteCode.vehicle && !inviteCode.vehicle.active) {
              inviteCodeVehicle = inviteCode.vehicle;
              vehicleId = inviteCode.vehicle._id;
              break;
            }
          }
        }
      } catch (error) {
        console.log("No invite codes found or error fetching:", error);
      }

      // Fallback to existing vehicle if no invite code vehicle found
      if (!vehicleId && customer.vehicles && customer.vehicles.length > 0) {
        vehicleId = customer.vehicles[0]._id;
      }

      // Activate customer with vehicle details (backend will check invite codes and use correct vehicle)
      const response = await api(`v1/customer/${customer._id}/activate`, "POST", {
        vehicleId: vehicleId, // null if no vehicle exists, backend will check invite codes
        vehicleDetails: {
          vehicleRegistration: values.vehicleDetails.vehicleRegistration,
          vin: values.vehicleDetails.vin,
          make: values.vehicleDetails.make,
          model: values.vehicleDetails.model,
          vehicleState: values.vehicleDetails.vehicleState,
          color: values.vehicleDetails.color || null,
          vehicleType: values.vehicleDetails.vehicleType || null
        }
      });

      if (response.data.success) {
        callback();
        closeModal();
      } else {
        setError(response.data.message || "Failed to activate customer");
      }
    } catch (error) {
      console.error("Error activating customer:", error);
      setError(error.response?.data?.message || "Failed to activate customer. Please try again.");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched }) => (
        <Form className="flex flex-column gap-4 w-full">
          <div className="text-sm text-gray-600 mb-4">
            Enter vehicle details to activate this customer. The customer, vehicle, and subscriptions will be activated.
          </div>

          {/* Customer Information Display */}
          <div className="p-4 bg-gray-50 rounded-lg mb-4">
            <h4 className="font-semibold mb-2">Customer Information</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="font-medium">Name:</span> {customer.firstName} {customer.lastName}
              </div>
              <div>
                <span className="font-medium">Email:</span> {customer.email}
              </div>
              <div>
                <span className="font-medium">Phone:</span> {customer.phone}
              </div>
              <div>
                <span className="font-medium">Status:</span>{" "}
                <span className={customer.active ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                  {customer.active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
            {existingVehicle && (
              <div className="mt-2 pt-2 border-t">
                <h5 className="font-medium mb-1">Existing Vehicle:</h5>
                <div className="text-sm">
                  <span className="font-medium">Status:</span>{" "}
                  <span className={existingVehicle.active ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                    {existingVehicle.active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <TextField
            label="Vehicle Registration"
            name="vehicleDetails.vehicleRegistration"
            variant="outlined"
            fullWidth
            value={values.vehicleDetails.vehicleRegistration}
            onChange={(e) => {
              setFieldValue("vehicleDetails.vehicleRegistration", e.target.value);
              if (values.vehicleDetails.vehicleState) {
                handleVehicleStateChange(setFieldValue, e.target.value, values.vehicleDetails.vehicleState);
              }
            }}
            error={touched.vehicleDetails?.vehicleRegistration && !!errors.vehicleDetails?.vehicleRegistration}
            helperText={touched.vehicleDetails?.vehicleRegistration && errors.vehicleDetails?.vehicleRegistration}
            required
          />

          <div>
            <label htmlFor="vehicleState" className="block mb-2 text-sm font-medium">
              {getVehicleStateFieldLabel()} <span className="text-red-500">*</span>
            </label>
            <Field
              as="select"
              id="vehicleState"
              name="vehicleDetails.vehicleState"
              onChange={(e) => {
                const state = e.target.value;
                setFieldValue("vehicleDetails.vehicleState", state);
                if (values.vehicleDetails.vehicleRegistration) {
                  handleVehicleStateChange(setFieldValue, values.vehicleDetails.vehicleRegistration, state);
                }
              }}
              className="block w-full px-4 py-3 text-base text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">---SELECT {getVehicleStateFieldLabel().toUpperCase()}---</option>
              {getVehicleStateOptionsForSelect(values.vehicleDetails.vehicleState).map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Field>
            <ErrorMessage name="vehicleDetails.vehicleState" component="div" className="text-red-500 text-sm mt-1" />
          </div>

          <TextField
            label="VIN"
            name="vehicleDetails.vin"
            variant="outlined"
            fullWidth
            value={values.vehicleDetails.vin}
            onChange={(e) => setFieldValue("vehicleDetails.vin", e.target.value.toUpperCase())}
            inputProps={{ maxLength: 17 }}
            error={touched.vehicleDetails?.vin && !!errors.vehicleDetails?.vin}
            helperText={touched.vehicleDetails?.vin && errors.vehicleDetails?.vin}
            required
          />

          <TextField
            label="Make"
            name="vehicleDetails.make"
            variant="outlined"
            fullWidth
            value={values.vehicleDetails.make}
            onChange={(e) => setFieldValue("vehicleDetails.make", e.target.value)}
            error={touched.vehicleDetails?.make && !!errors.vehicleDetails?.make}
            helperText={touched.vehicleDetails?.make && errors.vehicleDetails?.make}
            required
          />

          <TextField
            label="Model"
            name="vehicleDetails.model"
            variant="outlined"
            fullWidth
            value={values.vehicleDetails.model}
            onChange={(e) => setFieldValue("vehicleDetails.model", e.target.value)}
            error={touched.vehicleDetails?.model && !!errors.vehicleDetails?.model}
            helperText={touched.vehicleDetails?.model && errors.vehicleDetails?.model}
            required
          />

          <TextField
            label="Color"
            name="vehicleDetails.color"
            variant="outlined"
            fullWidth
            value={values.vehicleDetails.color || ""}
            onChange={(e) => setFieldValue("vehicleDetails.color", e.target.value)}
          />

          <div>
            <label htmlFor="vehicleType" className="block mb-2 text-sm font-medium">
              Vehicle Type
            </label>
            <Field
              as="select"
              id="vehicleType"
              name="vehicleDetails.vehicleType"
              className="block w-full px-4 py-3 text-base text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">---SELECT VEHICLE TYPE---</option>
              {VEHICLE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Field>
          </div>

          {error && (
            <div className="text-red-500 text-sm p-2 bg-red-50 rounded">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-4 mt-4">
            <Button
              variant="outlined"
              onClick={closeModal}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? "Activating..." : "Activate Customer"}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ActivateCustomerForm;

